import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, fileUrl, mediaSrc, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import PinToSweeperButton from "@/components/PinToSweeperButton";
import ReopenAndPinButton from "@/components/ReopenAndPinButton";
import { AdjustTimesButton } from "@/components/AdjustTimesButton";
import { EditJobModal } from "@/pages/Admin";
import { PartsStatusPicker } from "@/pages/PartsQueue";
import InlineWoFix from "@/components/InlineWoFix";
import WoAnswerSheet from "@/components/WoAnswerSheet";
import LegalProtocolPanel from "@/components/LegalProtocolPanel";
import AskTenantPanel from "@/components/AskTenantPanel";
import PmrRebillCard from "@/components/PmrRebillCard";

/**
 * Admin Desktop Job Detail — laptop-optimized 2-column view.
 * Approved mockup: /public/mockups/admin-job-desktop.html
 * Fallback: `?v=classic` renders the legacy AdminJobDetail from Admin.jsx.
 *
 * Layout:
 *   LEFT  → Hero (gradient) + inline warning banners + WO summary + 4-metric
 *           tiles + Photos grid + Notes card + Timeline
 *   RIGHT → Sticky sidebar: admin toggles, 6 shortcut buttons, Landlord Quote,
 *           Also-Nearby WOs
 *
 * All API endpoints already exist — no backend changes required.
 */
const JOB_CATEGORY_OPTIONS = [
  "Plumbing", "Electrical", "HVAC", "Roof", "Appliance",
  "High Water Usage", "Windows/Doors", "Drywall/Paint",
  "Landscaping", "Pest", "General Maintenance",
].map(c => ({ value: c, label: c }));

export default function AdminJobDesktop() {
  const { id } = useParams();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [nearby, setNearby] = useState([]);
  const [tick, setTick] = useState(0);
  const [workers, setWorkers] = useState([]);

  const load = () => api.get(`/jobs/${id}`).then(r => setJob(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    api.get(`/jobs/${id}/nearby`).then(r => setNearby((r.data || []).slice(0, 5))).catch(() => setNearby([]));
  }, [id]);

  // Photos: multi-select mode, trash modal, and helper handlers used by the
  // Photos card. Keeps a single set-of-file_ids as the selection ground truth.
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [trashOpen, setTrashOpen] = useState(false);
  const toggleSelected = (fid) => {
    if (!fid) return;
    setSelected(prev => {
      const next = new Set(prev);
      next.has(fid) ? next.delete(fid) : next.add(fid);
      return next;
    });
  };
  const deletePhoto = async (fid, kind) => {
    if (!fid) { toast.error("This photo has no file id — try refreshing"); return; }
    if (!window.confirm(`Delete this ${kind} photo? You can restore it from the Trash within 30 days.`)) return;
    try {
      await api.delete(`/jobs/${id}/photo/${fid}`);
      toast.success("Photo deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete");
    }
  };
  const movePhoto = async (fid, to) => {
    if (!fid) { toast.error("This photo has no file id — try refreshing"); return; }
    try {
      await api.post(`/jobs/${id}/photo/${fid}/move`, { to });
      toast.success(`Moved to ${to}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't move");
    }
  };
  const bulkDelete = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} photo${ids.length === 1 ? "" : "s"}? You can restore from the Trash within 30 days.`)) return;
    try {
      await api.post(`/jobs/${id}/photos/bulk-delete`, { file_ids: ids });
      toast.success(`Deleted ${ids.length} photo${ids.length === 1 ? "" : "s"}`);
      setSelected(new Set());
      setSelectMode(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't delete");
    }
  };

  // Metric tile → smooth-scroll to the matching card with a brief cyan
  // pulse so it's obvious where the tap landed. Cards register their DOM
  // id below (desk-photos, desk-receipts, desk-notes, desk-materials).
  const jumpToCard = (cardId) => {
    const el = document.getElementById(cardId);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    el.classList.add("ring-4", "ring-[#00C4CC]");
    setTimeout(() => el.classList.remove("ring-4", "ring-[#00C4CC]"), 1400);
  };

  // WO field editor — opens the shared EditJobModal (title, description,
  // address, unit, customer, priority, scheduled, category, entry code…).
  const [editOpen, setEditOpen] = useState(false);
  useEffect(() => { api.get("/assignable-users").then(r => setWorkers(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { const iv = setInterval(() => setTick(x => x + 1), 30000); return () => clearInterval(iv); }, []);

  // Keyboard shortcuts: C=chat, P=photos, ⌘K bypassed (existing global handler)
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (e.key.toLowerCase() === "c") {
        document.querySelector('[data-testid="desk-open-chat"]')?.click();
      } else if (e.key.toLowerCase() === "p") {
        document.getElementById("desk-photos")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!job) return <AdminShell><div className="p-8 font-bold" data-testid="desk-loading">Loading…</div></AdminShell>;

  const done = job.status === "completed";
  const activeOnClock = !!job.clock_in_at && !job.clock_out_at;
  const isTurn = !!job.is_turn;

  const fmtTime = (iso) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const fmtDate = (iso) => new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });

  const timerMins = (() => {
    if (!job.clock_in_at) return 0;
    const start = new Date(job.clock_in_at).getTime();
    const end = job.clock_out_at ? new Date(job.clock_out_at).getTime() : Date.now();
    return Math.max(0, Math.floor((end - start) / 60000));
  })();
  void tick;

  const beforeCount = (job.before_photos || []).length;
  const afterCount = (job.after_photos || []).length;
  const receiptCount = (job.receipts || []).length;
  const notesLen = ((job.work_performed_polished || job.work_performed || "").trim() || "").length;
  const materialsTotal = (job.materials || []).reduce((s, m) => s + (m.quantity || 0) * (m.unit_cost || 0), 0);
  const materialsRuns = (job.material_runs || []).length;

  const statusColor = done ? "text-emerald-600" : activeOnClock ? "text-emerald-600" : "text-slate-600";

  const toggleHabitability = async () => {
    try {
      const r = await api.post(`/jobs/${id}/toggle-habitability`, { value: !job.habitability });
      setJob(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleLocationGate = async () => {
    try {
      const r = await api.post(`/jobs/${id}/toggle-location-gate`, { disabled: !job.location_gate_disabled });
      setJob(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const doReopen = async () => {
    try {
      const r = await api.post(`/jobs/${id}/reopen`);
      setJob(r.data); toast.success("Reopened");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const habitabilityDays = (() => {
    if (!job.habitability || !job.habitability_started_at) return null;
    const start = new Date(job.habitability_started_at).getTime();
    const days = Math.floor((Date.now() - start) / (1000 * 60 * 60 * 24));
    return { day: days, total: 30 };
  })();

  return (
    <AdminShell>
      <div className="max-w-[1440px] mx-auto" data-testid="admin-job-desktop">
        {/* Top bar / breadcrumb */}
        <div className="sticky top-0 z-30 bg-white px-5 py-3 flex items-center gap-4 border-b border-slate-200">
          <div className="text-xs text-slate-500 font-medium">
            <Link to="/admin/jobs" className="text-[#008a8f] hover:underline font-semibold">Work Orders</Link>
            {" / "}
            <b className="text-[#0B1F3A]">WO {job.work_order_number || job.id?.slice(0, 6)}</b>
            <InlineWoFix jobId={job.id || job._id} currentWo={job.work_order_number || ""} onSaved={load} variant="row"/>
          </div>
          <h1 className="text-lg font-bold text-[#0B1F3A] truncate">
            {job.property_address}{job.unit ? ` · Unit ${job.unit}` : ""}
          </h1>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-[11px] text-slate-500 font-medium hidden lg:block">
              <kbd className="bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 font-mono text-[10px]">C</kbd> chat
              {" · "}
              <kbd className="bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 font-mono text-[10px]">P</kbd> photos
            </div>
            <button
              type="button"
              data-testid="desk-sync-clickup-btn"
              title="Pull real status, address hint, photos + notes from the ClickUp source task"
              onClick={async () => {
                try {
                  const r = await api.post(`/clickup/jobs/${id}/sync-now`);
                  const parts = [`ClickUp: ${r.data.cu_status}`, `FR: ${r.data.fr_status}`];
                  if (r.data.photos_pulled) parts.push(`+${r.data.photos_pulled} photos`);
                  if (r.data.attachments_pulled) parts.push(`+${r.data.attachments_pulled} files`);
                  if (r.data.completion_notes_pulled) parts.push("notes ✓");
                  toast.success(parts.join(" · "));
                  load();
                } catch (e) {
                  toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No ClickUp task found");
                }
              }}
              className="h-8 px-3 border-2 border-cyan-500 bg-cyan-50 hover:bg-cyan-100 text-cyan-900 font-black uppercase tracking-widest text-[10px] rounded-md inline-flex items-center gap-1">
              🔄 Sync from ClickUp
            </button>
            <PinToSweeperButton jobIds={[id]} workers={workers} onPinned={load}/>
            {job?.status === "completed" && (
              <ReopenAndPinButton jobId={id} workers={workers} onDone={load}/>
            )}
            {job?.status === "completed" && (job.combined_from || []).length > 0 && (
              <div className="w-full p-3 bg-rose-50 border-2 border-rose-400 rounded-lg" data-testid="desk-complete-close-af-nag">
                <div className="text-[11px] font-black uppercase tracking-widest text-rose-800 mb-1">
                  ⚠️ Don't forget — close these in AppFolio
                </div>
                <div className="text-xs text-rose-800 mb-2">This parent WO was merged from {(job.combined_from || []).length} AF tickets. Close each one manually in AppFolio so nothing sits open forever.</div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(job.combined_from || []).map((c, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-rose-400 rounded text-[11px] font-black font-mono text-rose-900">
                      WO {c.work_order_number || c.id.slice(-6)}
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  data-testid="desk-copy-af-numbers-complete"
                  onClick={() => {
                    const nums = (job.combined_from || []).map(c => c.work_order_number).filter(Boolean).join(", ");
                    navigator.clipboard.writeText(nums);
                    toast.success(`Copied ${(job.combined_from || []).length} AF#s to paste into AppFolio`);
                  }}
                  className="h-8 px-3 bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-black uppercase tracking-widest rounded"
                >⧉ Copy AF# list for AppFolio</button>
              </div>
            )}
            <Link to={`/admin/jobs/${id}?v=classic`} data-testid="desk-open-classic"
              className="text-[11px] font-bold text-slate-500 hover:text-[#0B1F3A] underline">
              Classic view
            </Link>
          </div>
        </div>

        <div className="grid gap-5 p-5 grid-cols-1 lg:[grid-template-columns:minmax(0,1fr)_380px]">
          {/* LEFT COLUMN */}
          <div className="min-w-0">
            {/* Pinned WO Answer Sheet — must sit above everything so the
                complaint / human notes / what-was-done / assignee are the
                first thing anyone opening the WO reads. */}
            <WoAnswerSheet job={job} onRefresh={load}/>
            <div className="mt-3">
              <LegalProtocolPanel jobId={id}/>
            </div>
            <AskTenantPanel jobId={id} job={job}/>
            {/* Hero */}
            <div className="rounded-2xl p-6 text-white flex items-start gap-5 mb-4 shadow-sm"
              style={{ background: "linear-gradient(135deg, rgba(0,196,204,0.12) 0%, rgba(11,31,58,0.9) 100%), linear-gradient(135deg, #0B1F3A, #00C4CC)" }}
              data-testid="desk-hero">
              <div className="flex-1">
                <div className="text-2xl font-bold tracking-tight" data-testid="desk-hero-address">
                  {job.property_address || job.title || "—"}
                </div>
                <div className="text-sm opacity-90 mt-1">
                  {job.unit ? `Unit ${job.unit} · ` : ""}{job.city || ""}{job.state ? `, ${job.state}` : ""}{job.zip ? ` ${job.zip}` : ""}
                </div>
                <div className="flex gap-3 flex-wrap mt-3 text-xs opacity-95">
                  {job.assigned_to_name && <span><b>{job.assigned_to_name}</b> assigned</span>}
                  {job.clock_in_at && <span>· Clocked in <b>{fmtTime(job.clock_in_at)}</b></span>}
                  {timerMins > 0 && <span>· <b>{Math.floor(timerMins / 60)}h {timerMins % 60}m</b> on clock</span>}
                  {job.appfolio_wo_id && <span>· Src <b>AF-{job.appfolio_wo_id}</b></span>}
                </div>
                <div className="mt-3">
                  <AdjustTimesButton
                    job={job}
                    onSaved={() => load && load()}
                    testid="desk-adjust-times-btn"
                  />
                </div>
              </div>
              <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                <span className={`inline-flex items-center gap-1.5 bg-white/95 px-3 py-1 rounded-full text-[11px] font-bold ${statusColor}`}
                  data-testid="desk-status-pill">
                  <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse"/>
                  {done ? "Completed" : activeOnClock ? "In progress" : (job.status || "").replace("_", " ")}
                </span>
                {job.priority && (
                  <span className="text-[11px] opacity-85">Priority: <b>{job.priority}</b></span>
                )}
              </div>
            </div>

            {/* Inline warning banners */}
            {habitabilityDays && (
              <div className="p-3 rounded-xl mb-3 flex items-start gap-2.5 text-xs bg-red-50 text-red-800 border border-red-200"
                data-testid="desk-habitability-banner">
                <span className="text-base">🚨</span>
                <div><b>Habitability clock running.</b> 30-day repair window · day <b>{habitabilityDays.day}</b> of {habitabilityDays.total}</div>
              </div>
            )}
            {job.funding_pending && (
              <div className="p-3 rounded-xl mb-3 flex items-start gap-2.5 text-xs bg-amber-50 text-amber-800 border border-amber-200"
                data-testid="desk-funding-banner">
                <span className="text-base">💵</span>
                <div><b>Funding pending.</b> Quote signed but not funded — clock-in requires override</div>
              </div>
            )}
            {job.same_building_flag && (
              <div className="p-3 rounded-xl mb-3 flex items-start gap-2.5 text-xs bg-blue-50 text-blue-800 border border-blue-200"
                data-testid="desk-duplicates-banner">
                <span className="text-base">🏢</span>
                <div><b>Same-building WOs open.</b> {(job.same_building_refs || []).length} other WOs at this address</div>
              </div>
            )}

            {/* WO summary */}
            <Card
              title="Work Order"
              testid="desk-wo-summary"
              action={
                <button type="button" onClick={() => setEditOpen(true)}
                  data-testid="desk-wo-edit"
                  className="h-8 px-3 border-2 border-[#0f172a] bg-white hover:bg-slate-100 text-slate-900 text-[10px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                  ✎ Edit
                </button>
              }
            >
              <EditableRow lbl="Title" value={job.title || ""}
                field="title" jobId={id} onSaved={load}
                testid="desk-edit-title" placeholder="Short title of the work order"/>
              <EditableRow lbl="Description" value={job.description || ""}
                field="description" jobId={id} onSaved={load} type="textarea"
                testid="desk-edit-description" placeholder="What needs to be done?"/>
              <Row lbl="Source" val={job.source ? <>{job.source} <small className="text-slate-500">· received {job.created_at ? fmtDate(job.created_at) : ""}</small></> : "—"}/>
              <EditableRow lbl="Scheduled" value={job.scheduled_for ? String(job.scheduled_for).slice(0, 10) : ""}
                displayValue={job.scheduled_for || <span className="text-slate-400 italic">Unscheduled — tap to set</span>}
                field="scheduled_for" jobId={id} onSaved={load} type="date"
                testid="desk-edit-scheduled"/>
              <EditableRow lbl="Priority" value={job.priority || "normal"}
                displayValue={<span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${job.priority === "urgent" ? "bg-red-600 text-white" : job.priority === "high" ? "bg-amber-500 text-white" : job.priority === "low" ? "bg-slate-200 text-slate-600" : "bg-slate-100 text-slate-700"}`}>{job.priority || "normal"}</span>}
                field="priority" jobId={id} onSaved={load} type="select"
                options={[
                  { value: "low", label: "Low" },
                  { value: "normal", label: "Normal" },
                  { value: "high", label: "High" },
                  { value: "urgent", label: "Urgent" },
                ]}
                testid="desk-edit-priority"/>
              <EditableRow lbl="Category" value={job.category || ""}
                field="category" jobId={id} onSaved={load} type="select"
                options={JOB_CATEGORY_OPTIONS}
                testid="desk-edit-category"/>
              <div className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0" data-testid="desk-parts-row">
                <div className="text-xs text-slate-500 w-32 font-medium flex-shrink-0 pt-1">Parts</div>
                <div className="flex-1 min-w-0">
                  <PartsStatusPicker
                    job={job}
                    currentStatus={job.parts_status}
                    currentNote={job.parts_note || ""}
                    onSaved={load}
                  />
                  {job.parts_note && (
                    <div className="mt-2 text-xs text-slate-600 italic">📝 {job.parts_note}</div>
                  )}
                </div>
              </div>
              {job.ai_triage?.summary && <Row lbl="AI triage" val={<span className="text-slate-600 font-normal">{job.ai_triage.summary}</span>}/>}
            </Card>

            {/* Metric tiles */}
            <div className="grid grid-cols-4 gap-3 mb-4">
              <Metric label="Photos" value={<>{beforeCount} <small>before</small> / {afterCount} <small>after</small></>} testid="desk-metric-photos" onClick={() => jumpToCard("desk-photos")}/>
              <Metric label="Receipts" value={<>{receiptCount} <small>{receiptCount === 0 && !job.no_purchase ? "missing" : "attached"}</small></>} testid="desk-metric-receipts" onClick={() => jumpToCard("desk-receipts")}/>
              <Metric label="Notes" value={<>{notesLen} <small>chars {notesLen >= 30 ? "✓" : ""}</small></>} testid="desk-metric-notes" onClick={() => jumpToCard("desk-notes")}/>
              <Metric label="Materials" value={<>${materialsTotal.toFixed(2)} <small>({materialsRuns} run{materialsRuns === 1 ? "" : "s"})</small></>} testid="desk-metric-materials" onClick={() => jumpToCard("desk-materials")}/>
            </div>

            {/* AF numbers audit strip — visible when this parent was merged
                from multiple AppFolio WOs. Copy button lets you paste back
                into AppFolio for bulk-close so no ticket falls through the
                cracks. */}
            {(job.combined_from || []).length > 0 && (
              <div className="p-3 mb-4 bg-amber-50 border-2 border-amber-300 rounded-lg" data-testid="desk-combined-from-strip">
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <div className="text-[10px] font-black uppercase tracking-widest text-amber-800">
                    🧾 Combined from · {(job.combined_from || []).length} AppFolio WO{(job.combined_from || []).length === 1 ? "" : "s"}
                  </div>
                  <button
                    type="button"
                    data-testid="desk-copy-af-numbers"
                    onClick={() => {
                      const nums = (job.combined_from || []).map(c => c.work_order_number).filter(Boolean).join(", ");
                      navigator.clipboard.writeText(nums);
                      toast.success(`Copied ${(job.combined_from || []).length} AF#s: ${nums.slice(0, 60)}${nums.length > 60 ? "…" : ""}`);
                    }}
                    className="h-7 px-2 bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-black uppercase tracking-widest rounded"
                  >⧉ Copy all AF#s</button>
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="desk-combined-from-chips">
                  {(job.combined_from || []).map((c, i) => (
                    <Link
                      key={i}
                      to={`/admin/jobs/${c.id}`}
                      data-testid={`desk-combined-chip-${i}`}
                      title={`Open the original merged WO ${c.work_order_number || ""}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-white hover:bg-amber-100 border border-amber-400 rounded text-[11px] font-black font-mono text-amber-900"
                    >
                      WO {c.work_order_number || c.id.slice(-6)}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Combined tickets (checklist_items) — each merged ticket
                keeps its identity as its own line here. Split any line to a
                new sibling WO with the ↑ button. */}
            {(job.checklist_items || []).length > 0 && (
              <Card
                title="Combined tickets"
                count={String((job.checklist_items || []).length)}
                id="desk-checklist-items"
                testid="desk-checklist-items-card"
              >
                <ul className="space-y-2">
                  {(job.checklist_items || []).map((it, i) => (
                    <li key={it.id} data-testid={`desk-checklist-item-${it.id}`}
                        className={`p-3 border-2 rounded-lg flex items-start gap-3 ${it.done ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200"}`}>
                      <button type="button" onClick={async () => {
                          try {
                            await api.patch(`/jobs/${id}/checklist/${it.id}`, { done: !it.done });
                            toast.success(it.done ? "Reopened" : "Marked done");
                            load();
                          } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update"); }
                        }}
                        data-testid={`desk-checklist-toggle-${it.id}`}
                        className={`h-6 w-6 flex-shrink-0 border-2 flex items-center justify-center ${it.done ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-400 hover:border-emerald-500"}`}>
                        {it.done && "✓"}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-semibold ${it.done ? "line-through text-slate-500" : "text-slate-900"}`}>
                          {it.text}
                        </div>
                        <div className="mt-0.5 text-[11px] text-slate-500 flex flex-wrap gap-x-2">
                          {it.source_wo_number && <span>From <b>WO {it.source_wo_number}</b></span>}
                          {it.life_safety && <span className="text-red-600 font-black">⚠ Life Safety</span>}
                          {it.eta_at && <span>ETA <b>{new Date(it.eta_at).toLocaleDateString()}</b></span>}
                          {(it.before_photos?.length + it.after_photos?.length) > 0 && (
                            <span>{(it.before_photos?.length || 0) + (it.after_photos?.length || 0)} photos</span>
                          )}
                        </div>
                        {it.description && (
                          <div className="mt-1 text-xs text-slate-600 whitespace-pre-wrap">{it.description}</div>
                        )}
                      </div>
                      <button type="button"
                        onClick={async () => {
                          const reason = window.prompt(`Split "${it.text}" to a new sibling WO? Optional reason (e.g. different vendor, different day):`, "");
                          if (reason === null) return;
                          const company = window.prompt("Vendor / company for the new WO (optional):", "");
                          try {
                            const { data } = await api.post(`/jobs/${id}/checklist/${it.id}/split`, {
                              reason: reason || "",
                              company: company || "",
                            });
                            toast.success(`Split to new WO ${data.new_wo.work_order_number || data.new_wo.id.slice(-6)}`);
                            load();
                          } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't split"); }
                        }}
                        data-testid={`desk-checklist-split-${it.id}`}
                        title="Split this ticket off to a new sibling WO with next suffix"
                        className="h-8 px-2 border-2 border-fuchsia-600 text-fuchsia-700 hover:bg-fuchsia-50 text-[10px] font-black uppercase tracking-widest flex-shrink-0">
                        ↑ Split
                      </button>
                    </li>
                  ))}
                </ul>
                {(job.split_history || []).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100 text-[11px] text-slate-500">
                    <b className="uppercase tracking-widest text-slate-400">Split history</b>
                    <ul className="mt-1 space-y-0.5">
                      {(job.split_history || []).map((h, i) => (
                        <li key={i}>
                          <b>{h.item_text}</b> → WO {h.new_wo_number || "new"} {h.reason ? `· ${h.reason}` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card>
            )}

            {/* Photos */}
            <Card
              title="Photos"
              count={`${beforeCount} · ${afterCount}`}
              id="desk-photos"
              testid="desk-photos-card"
              action={
                <div className="flex items-center gap-1.5">
                  {selectMode ? (
                    <>
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        {selected.size} selected
                      </span>
                      <button type="button" onClick={bulkDelete} disabled={selected.size === 0}
                        data-testid="desk-photos-bulk-delete"
                        className="h-8 px-2.5 border-2 border-red-600 bg-red-600 hover:bg-red-700 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-40">
                        🗑 Delete {selected.size || ""}
                      </button>
                      <button type="button" onClick={() => { setSelectMode(false); setSelected(new Set()); }}
                        data-testid="desk-photos-bulk-cancel"
                        className="h-8 px-2.5 border-2 border-slate-300 text-slate-700 text-[10px] font-black uppercase tracking-widest hover:bg-slate-100">
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" onClick={() => setSelectMode(true)}
                        disabled={beforeCount + afterCount === 0}
                        data-testid="desk-photos-select-mode"
                        className="h-8 px-2.5 border-2 border-[#0f172a] bg-white text-slate-900 text-[10px] font-black uppercase tracking-widest hover:bg-slate-100 disabled:opacity-40">
                        Select
                      </button>
                      <button type="button" onClick={() => setTrashOpen(true)}
                        data-testid="desk-photos-trash-open"
                        className="h-8 px-2.5 border-2 border-[#0f172a] bg-white text-slate-900 text-[10px] font-black uppercase tracking-widest hover:bg-slate-100">
                        🗑 Trash
                      </button>
                    </>
                  )}
                </div>
              }
            >
              {beforeCount + afterCount === 0 ? (
                <div className="text-sm text-slate-400 text-center py-6">No photos yet.</div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {[
                    ...(job.before_photos || []).map(p => ({ ...p, kind: "before" })),
                    ...(job.after_photos || []).map(p => ({ ...p, kind: "after" })),
                  ].map((p, i) => {
                    const isSel = selected.has(p.file_id);
                    return (
                      <div key={i}
                        className={`relative group aspect-square rounded-lg overflow-hidden bg-slate-200 ${isSel ? "ring-4 ring-cyan-500" : "hover:ring-2 hover:ring-[#00C4CC]"}`}
                        data-testid={`desk-photo-${i}`}>
                        {selectMode ? (
                          <button type="button" onClick={() => toggleSelected(p.file_id)}
                            className="block w-full h-full"
                            data-testid={`desk-photo-select-${i}`}>
                            <img src={mediaSrc(p)} alt="" className="w-full h-full object-cover"/>
                          </button>
                        ) : (
                          <a href={mediaSrc(p)} target="_blank" rel="noreferrer" className="block w-full h-full">
                            <img src={mediaSrc(p)} alt="" className="w-full h-full object-cover"/>
                          </a>
                        )}
                        <span className="absolute top-1 left-1 text-[8px] font-black uppercase tracking-widest bg-[#0f172a]/80 text-white px-1.5 py-0.5 rounded pointer-events-none">
                          {p.kind}
                        </span>
                        {selectMode ? (
                          <span className={`absolute top-1 right-1 h-6 w-6 rounded-full border-2 border-white flex items-center justify-center font-black text-xs shadow-lg ${isSel ? "bg-cyan-600 text-white" : "bg-white/70 text-slate-700"}`}>
                            {isSel ? "✓" : ""}
                          </span>
                        ) : (
                          <div className="absolute top-1 right-1 flex flex-col gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); movePhoto(p.file_id, p.kind === "before" ? "after" : "before"); }}
                              data-testid={`desk-photo-move-${i}`}
                              title={p.kind === "before" ? "Move to After" : "Move to Before"}
                              className="h-6 px-1.5 rounded bg-cyan-600 hover:bg-cyan-700 text-white text-[9px] font-black uppercase tracking-widest shadow-lg whitespace-nowrap">
                              → {p.kind === "before" ? "After" : "Before"}
                            </button>
                            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); deletePhoto(p.file_id, p.kind); }}
                              data-testid={`desk-photo-delete-${i}`}
                              title="Delete photo"
                              className="h-6 w-6 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-black flex items-center justify-center shadow-lg self-end">
                              ×
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
            {trashOpen && (
              <PhotoTrashModal jobId={job.id} onClose={() => setTrashOpen(false)} onRestored={() => { load(); }}/>
            )}
            {editOpen && (
              <EditJobModal job={job} onClose={() => setEditOpen(false)} onSaved={async () => { setEditOpen(false); await load(); }}/>
            )}

            {/* PMR Rebill — vendor invoice + ProCall markup → PMR PDF */}
            <PmrRebillCard jobId={id}/>

            {/* Receipts */}
            <Card
              title="Receipts"
              count={String(receiptCount)}
              id="desk-receipts"
              testid="desk-receipts-card"
            >
              {(job.receipts || []).length === 0 ? (
                <div className="text-sm text-slate-400 text-center py-6">
                  {job.no_purchase ? "Marked as no-purchase — no receipts needed." : "No receipts attached yet."}
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {(job.receipts || []).map((r, i) => (
                    <div key={i} className="relative group aspect-square rounded-lg overflow-hidden bg-slate-200 hover:ring-2 hover:ring-[#00C4CC]"
                      data-testid={`desk-receipt-${i}`}>
                      <a href={mediaSrc(r)} target="_blank" rel="noreferrer" className="block w-full h-full">
                        {(r.content_type || "").startsWith("image/") ? (
                          <img src={mediaSrc(r)} alt="" className="w-full h-full object-cover"/>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-3xl text-slate-500 bg-slate-100">📄</div>
                        )}
                      </a>
                      <span className="absolute top-1 left-1 text-[8px] font-black uppercase tracking-widest bg-[#0f172a]/80 text-white px-1.5 py-0.5 rounded pointer-events-none">
                        Receipt
                      </span>
                      {r.amount != null && (
                        <span className="absolute bottom-1 left-1 right-1 text-[10px] font-black bg-emerald-600 text-white px-1 py-0.5 rounded text-center">
                          ${Number(r.amount).toFixed(2)}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.preventDefault(); e.stopPropagation();
                          if (!r.file_id) { toast.error("Receipt has no file id"); return; }
                          if (!window.confirm("Delete this receipt? You can restore from the Trash within 30 days.")) return;
                          try {
                            await api.delete(`/jobs/${job.id}/photo/${r.file_id}`);
                            toast.success("Receipt deleted");
                            load();
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Couldn't delete");
                          }
                        }}
                        data-testid={`desk-receipt-delete-${i}`}
                        title="Delete receipt"
                        className="absolute top-1 right-1 h-6 w-6 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-black flex items-center justify-center opacity-0 group-hover:opacity-100 shadow-lg">
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Notes */}
            <Card title="Tech notes" id="desk-notes" testid="desk-notes-card">
              <div className="bg-slate-50 rounded-xl p-4 text-sm leading-relaxed text-slate-700" data-testid="desk-notes">
                {job.work_performed_polished || job.work_performed || <span className="text-slate-400">No notes yet.</span>}
              </div>
            </Card>

            {/* Materials */}
            <Card
              title="Materials"
              count={String(materialsRuns)}
              id="desk-materials"
              testid="desk-materials-card"
              action={<span className="text-xs font-black text-emerald-700">${materialsTotal.toFixed(2)} total</span>}
            >
              {(job.materials || []).length === 0 ? (
                <div className="text-sm text-slate-400 text-center py-6">No materials logged yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] font-black uppercase tracking-widest text-slate-500 border-b border-slate-200">
                        <th className="py-1.5 pr-2">Item</th>
                        <th className="py-1.5 pr-2 text-right">Qty</th>
                        <th className="py-1.5 pr-2 text-right">Unit</th>
                        <th className="py-1.5 pr-2 text-right">Total</th>
                        <th className="py-1.5 pr-2">Added by</th>
                        <th className="py-1.5 w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(job.materials || []).map((m, i) => {
                        const line = (Number(m.quantity) || 0) * (Number(m.unit_cost) || 0);
                        return (
                          <tr key={m.id || i} className="border-b border-slate-100 last:border-0" data-testid={`desk-material-${i}`}>
                            <td className="py-1.5 pr-2 font-semibold">{m.name || "—"}</td>
                            <td className="py-1.5 pr-2 text-right">{m.quantity ?? "—"}</td>
                            <td className="py-1.5 pr-2 text-right">${(Number(m.unit_cost) || 0).toFixed(2)}</td>
                            <td className="py-1.5 pr-2 text-right font-black">${line.toFixed(2)}</td>
                            <td className="py-1.5 pr-2 text-xs text-slate-500 truncate">{m.added_by_name || "—"}</td>
                            <td className="py-1.5 text-right">
                              {m.id && (
                                <button type="button"
                                  onClick={async () => {
                                    if (!window.confirm(`Remove "${m.name}" from materials?`)) return;
                                    try {
                                      await api.delete(`/jobs/${id}/materials/${m.id}`);
                                      toast.success("Material removed");
                                      load();
                                    } catch (err) {
                                      toast.error(err?.response?.data?.detail || "Couldn't delete");
                                    }
                                  }}
                                  data-testid={`desk-material-delete-${i}`}
                                  title="Remove material"
                                  className="h-6 w-6 rounded bg-red-100 hover:bg-red-200 text-red-700 text-xs font-black">
                                  ×
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* Timeline */}
            <Card title="Timeline" testid="desk-timeline-card">
              <Timeline job={job} fmtTime={fmtTime} fmtDate={fmtDate}/>
            </Card>
          </div>

          {/* RIGHT SIDEBAR */}
          <div className="sticky top-[70px] self-start max-h-[calc(100vh-90px)] overflow-y-auto" data-testid="desk-sidebar">
            {/* Admin controls */}
            <Card title="Admin controls">
              <AdmRow label="🏠 Habitability clock" sub={habitabilityDays ? `Day ${habitabilityDays.day} of ${habitabilityDays.total} — SHHA` : "Starts SHHA 30-day clock"}>
                <Toggle on={!!job.habitability} onChange={toggleHabitability} testid="desk-toggle-habitability"/>
              </AdmRow>
              <AdmRow label="📍 Location gate" sub="Tech must be within radius">
                <Toggle on={!job.location_gate_disabled} onChange={toggleLocationGate} testid="desk-toggle-locationgate"/>
              </AdmRow>
              <AdmRow label="💵 Funding guard" sub={job.funding_pending ? "Quote signed · unpaid" : "OK — no blocks"}>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${job.funding_pending ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                  {job.funding_pending ? "PENDING" : "CLEAR"}
                </span>
              </AdmRow>
              <AdmRow label="🛡 Insurance coverage" sub={job.insurance_coverage ? "Warranty may apply" : "No coverage flagged"}>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${job.insurance_coverage ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
                  {job.insurance_coverage ? "FLAG" : "—"}
                </span>
              </AdmRow>

              <div className="mt-4 grid grid-cols-2 gap-1.5">
                <ActionBtn to="/admin/team-chat" testid="desk-open-chat" icon="💬">Open chat</ActionBtn>
                <ActionBtn to={`/admin/jobs/${id}?v=classic#otp`} testid="desk-send-otp" icon="🔑">Send OTP</ActionBtn>
                <ActionBtn to={`/admin/jobs/${id}?v=classic#pdf`} testid="desk-compliance-pdf" icon="📄">Compliance PDF</ActionBtn>
                <ActionBtn to={`/admin/jobs/${id}?v=classic#review`} testid="desk-send-review" icon="⭐">Send review</ActionBtn>
                {done ? (
                  <button onClick={doReopen} data-testid="desk-reopen"
                    className="h-9 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-[#0B1F3A] text-xs font-semibold inline-flex items-center gap-1.5">
                    ↺ Reopen
                  </button>
                ) : (
                  <ActionBtn to={`/admin/jobs/${id}?v=classic#reopen`} testid="desk-reopen-link" icon="↺">Reopen</ActionBtn>
                )}
                {isTurn && (
                  <ActionBtn to={`/admin/jobs/${id}?v=classic#chargeback`} testid="desk-chargeback" icon="📊">Chargeback</ActionBtn>
                )}
              </div>
            </Card>

            {/* Landlord quote */}
            {job.landlord_quote && (
              <Card title="Landlord quote"
                headerRight={<span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${job.landlord_quote.signed ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>
                  {job.landlord_quote.signed ? "SIGNED" : "DRAFT"}
                </span>}>
                <Row lbl="Labor" val={`$${(job.landlord_quote.labor_cents / 100).toFixed(2)}`}/>
                <Row lbl="Materials" val={`$${(job.landlord_quote.materials_cents / 100).toFixed(2)}`}/>
                <Row lbl="Markup" val={`${job.landlord_quote.markup_pct || 0}%`}/>
                <Row lbl="Total" val={<b className="text-[#0B1F3A] text-lg">${(job.landlord_quote.total_cents / 100).toFixed(2)}</b>}/>
                <Row lbl="Funded?" val={job.funding_pending
                  ? <span className="text-red-600 font-semibold">No · pending</span>
                  : <span className="text-emerald-600 font-semibold">Yes</span>}/>
                <div className="mt-3 flex gap-1.5">
                  <Link to={`/admin/jobs/${id}?v=classic#quote`} data-testid="desk-quote-send"
                    className="flex-1 h-9 rounded-lg bg-[#00C4CC] hover:bg-[#00a8af] text-white text-xs font-bold inline-flex items-center justify-center">
                    Send invoice
                  </Link>
                  <Link to={`/admin/jobs/${id}?v=classic#quote`} data-testid="desk-quote-edit"
                    className="h-9 px-3 rounded-lg bg-white border border-slate-200 text-[#0B1F3A] text-xs font-bold inline-flex items-center">
                    Edit
                  </Link>
                </div>
              </Card>
            )}

            {/* Also nearby */}
            {nearby.length > 0 && (
              <Card title="Also nearby" count={nearby.length} testid="desk-nearby-card">
                <div className="text-xs">
                  {nearby.map(n => (
                    <Link key={n.id} to={`/admin/jobs/${n.id}`} data-testid={`desk-nearby-${n.id}`}
                      className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 -mx-1 px-1 rounded">
                      <span>
                        <b className="text-[#0B1F3A]">WO {n.work_order_number || n.id?.slice(0, 6)}</b>
                        {" · "}
                        <span className="text-slate-600">{n.property_address}{n.unit ? ` · Unit ${n.unit}` : ""}</span>
                      </span>
                      {n.distance_mi != null && (
                        <span className="text-slate-400 text-[11px]">· {n.distance_mi.toFixed(1)} mi</span>
                      )}
                    </Link>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────
function Card({ title, count, headerRight, action, children, id, testid }) {
  const right = action || headerRight;
  return (
    <div id={id} data-testid={testid}
      className="bg-white rounded-xl p-5 shadow-sm mb-4">
      <h3 className="m-0 mb-3 text-[11px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
        {title}
        {count != null && <span className="bg-[#0B1F3A] text-white text-[10px] px-2 py-0.5 rounded-full font-bold">{count}</span>}
        {right && <span className="ml-auto">{right}</span>}
      </h3>
      {children}
    </div>
  );
}

/**
 * Photo Trash — 30-day recovery window. Backed by
 *   GET  /api/jobs/{id}/photo-trash
 *   POST /api/jobs/{id}/photo/{file_id}/restore
 */
function PhotoTrashModal({ jobId, onClose, onRestored }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/jobs/${jobId}/photo-trash`).then(r => setRows(r.data.rows || [])).catch(() => setRows([]));
  }, [jobId]);
  const restore = async (fid) => {
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/photo/${fid}/restore`);
      toast.success("Photo restored");
      setRows(prev => (prev || []).filter(r => r.file_id !== fid));
      onRestored && onRestored();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't restore");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 flex items-start sm:items-center justify-center z-[110] p-4 overflow-y-auto"
         onClick={onClose} data-testid="photo-trash-modal">
      <div onClick={e => e.stopPropagation()} className="w-full max-w-2xl bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a] p-6 my-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display font-black text-2xl">🗑 Photo Trash</h3>
            <p className="mt-1 text-xs text-slate-600 font-medium">
              Photos deleted from this WO in the last 30 days. Older deletions can't be recovered.
            </p>
          </div>
          <button onClick={onClose} className="p-1" data-testid="photo-trash-close">✕</button>
        </div>
        <div className="mt-4">
          {rows == null && <div className="text-sm text-slate-500 p-6 text-center">Loading…</div>}
          {rows != null && rows.length === 0 && (
            <div className="text-sm text-slate-500 p-8 text-center rugged-card">
              Trash is empty — no photos deleted in the last 30 days.
            </div>
          )}
          {rows && rows.length > 0 && (
            <div className="grid grid-cols-3 gap-3">
              {rows.map(r => (
                <div key={r.file_id} data-testid={`photo-trash-row-${r.file_id}`}
                     className="border-2 border-slate-300 rounded-lg overflow-hidden bg-slate-50">
                  <div className="aspect-square bg-slate-200">
                    <img src={mediaSrc(r)} alt="" className="w-full h-full object-cover"/>
                  </div>
                  <div className="p-2">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                      {r.bucket === "after_photos" ? "After" : r.bucket === "receipts" ? "Receipt" : "Before"}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5 truncate">
                      By <b>{r.deleted_by_name}</b> · {new Date(r.deleted_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </div>
                    <button onClick={() => restore(r.file_id)} disabled={busy}
                      data-testid={`photo-trash-restore-${r.file_id}`}
                      className="mt-2 w-full h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-40">
                      ↺ Restore
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ lbl, val }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
      <div className="text-xs text-slate-500 w-32 font-medium flex-shrink-0">{lbl}</div>
      <div className="text-sm font-semibold text-[#0B1F3A]">{val}</div>
    </div>
  );
}

/**
 * EditableRow — click the value (or the pencil ✎) to inline-edit a single
 * job field. Enter saves, Escape cancels, blur saves. Sends a PATCH to
 * /api/jobs/{id} with just the changed field.
 *
 * type:
 *   "text"     — single-line input
 *   "textarea" — multi-line input
 *   "select"   — dropdown; pass `options={[{value,label}]}`
 *   "date"     — HTML date input
 */
function EditableRow({ lbl, value, displayValue, field, type = "text", options, jobId, onSaved, testid, placeholder }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [busy, setBusy] = useState(false);

  const start = () => {
    setDraft(value ?? "");
    setEditing(true);
    // Focus after render — small timeout so the input actually mounts
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="${testid}-input"]`);
      if (el) { el.focus(); if (el.select) el.select(); }
    }, 20);
  };

  const cancel = () => { setDraft(value ?? ""); setEditing(false); };

  const save = async () => {
    const nextVal = (draft ?? "");
    const wasVal = value ?? "";
    if (String(nextVal).trim() === String(wasVal).trim()) { setEditing(false); return; }
    setBusy(true);
    try {
      // Empty string is a legit value (e.g. "clear category"). Send as-is.
      const payload = { [field]: nextVal === "" ? null : nextVal };
      await api.patch(`/jobs/${jobId}`, payload);
      toast.success(`${lbl} updated`);
      setEditing(false);
      onSaved && await onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || `Couldn't save ${lbl.toLowerCase()}`);
    } finally { setBusy(false); }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && type !== "textarea") { e.preventDefault(); save(); }
    else if (e.key === "Escape") { e.preventDefault(); cancel(); }
    else if (e.key === "Enter" && type === "textarea" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); save(); }
  };

  return (
    <div className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0 group" data-testid={testid}>
      <div className="text-xs text-slate-500 w-32 font-medium flex-shrink-0 pt-1">{lbl}</div>
      <div className="flex-1 min-w-0">
        {!editing ? (
          <button type="button" onClick={start}
            data-testid={`${testid}-start`}
            className="w-full text-left text-sm font-semibold text-[#0B1F3A] py-1 px-2 -mx-2 rounded hover:bg-cyan-50 hover:ring-1 hover:ring-cyan-300 cursor-text inline-flex items-center gap-2">
            <span className="min-w-0 truncate">{displayValue ?? value ?? <span className="text-slate-400 italic">— tap to add —</span>}</span>
            <span className="ml-auto text-cyan-600 opacity-0 group-hover:opacity-100 text-xs" aria-hidden>✎</span>
          </button>
        ) : (
          <div className="flex items-start gap-2">
            {type === "textarea" ? (
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKey} onBlur={save}
                placeholder={placeholder}
                data-testid={`${testid}-input`}
                rows={3}
                className="flex-1 min-w-0 border-2 border-[#00C4CC] rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#00C4CC]"/>
            ) : type === "select" ? (
              <select value={draft} onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKey} onBlur={save}
                data-testid={`${testid}-input`}
                className="flex-1 min-w-0 h-9 border-2 border-[#00C4CC] rounded px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#00C4CC]">
                <option value="">—</option>
                {(options || []).map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            ) : (
              <input type={type} value={draft} onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKey} onBlur={save}
                placeholder={placeholder}
                data-testid={`${testid}-input`}
                className="flex-1 min-w-0 h-9 border-2 border-[#00C4CC] rounded px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00C4CC]"/>
            )}
            <div className="flex gap-1 pt-0.5">
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={save} disabled={busy}
                data-testid={`${testid}-save`}
                title="Save (Enter)"
                className="h-9 px-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black rounded disabled:opacity-40">✓</button>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={cancel}
                data-testid={`${testid}-cancel`}
                title="Cancel (Esc)"
                className="h-9 px-2 bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-black rounded">✕</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, testid, onClick }) {
  const clickable = typeof onClick === "function";
  const Tag = clickable ? "button" : "div";
  return (
    <Tag
      type={clickable ? "button" : undefined}
      onClick={clickable ? onClick : undefined}
      data-testid={testid}
      className={`text-left w-full bg-slate-50 rounded-xl px-4 py-3 transition-all ${clickable ? "hover:bg-cyan-50 hover:ring-2 hover:ring-[#00C4CC] active:scale-[0.98] cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#00C4CC]" : ""}`}>
      <div className="text-[10px] text-slate-500 font-semibold uppercase tracking-widest">
        {label}{clickable && <span className="ml-1 text-[10px] text-cyan-600" aria-hidden>›</span>}
      </div>
      <div className="text-lg font-bold text-[#0B1F3A] mt-0.5">{value}</div>
    </Tag>
  );
}

function AdmRow({ label, sub, children }) {
  return (
    <div className="py-3 flex items-center gap-3 border-b border-slate-100">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[#0B1F3A]">{label}</div>
        {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

function Toggle({ on, onChange, testid }) {
  return (
    <button onClick={onChange} data-testid={testid}
      className={`w-[42px] h-[24px] rounded-full relative transition-colors flex-shrink-0
        ${on ? "bg-[#00C4CC]" : "bg-slate-300"}`}>
      <span className={`absolute top-0.5 w-[20px] h-[20px] rounded-full bg-white shadow-sm transition-transform
        ${on ? "translate-x-[20px]" : "translate-x-0.5"}`}/>
    </button>
  );
}

function ActionBtn({ to, testid, icon, children }) {
  return (
    <Link to={to} data-testid={testid}
      className="h-9 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-[#0B1F3A] text-xs font-semibold inline-flex items-center gap-1.5">
      <span>{icon}</span>
      <span className="truncate">{children}</span>
    </Link>
  );
}

function Timeline({ job, fmtTime, fmtDate }) {
  // Aggregate a coarse timeline from the job doc — falls back to activity_log if present.
  const items = useMemo(() => {
    const list = [];
    if (job.en_route_at) list.push({ at: job.en_route_at, msg: <><b>{job.assigned_to_name || "Tech"}</b> tapped On the way{job.en_route_eta_minutes ? ` · ETA ${job.en_route_eta_minutes} min` : ""}</> });
    if (job.clock_in_at) list.push({ at: job.clock_in_at, msg: <><b>{job.assigned_to_name || "Tech"}</b> clocked in</> });
    (job.material_runs || []).forEach((mr, i) => {
      list.push({ at: mr.at || mr.started_at, msg: <><b>{job.assigned_to_name || "Tech"}</b> logged material run #{i + 1}</> });
    });
    if (job.clock_out_at) list.push({ at: job.clock_out_at, msg: <><b>{job.assigned_to_name || "Tech"}</b> clocked out</> });
    if (job.completed_at) list.push({ at: job.completed_at, msg: <><b>Job completed</b></> });
    (job.activity_log || []).slice(-8).forEach(log => {
      list.push({ at: log.at, msg: <><b>{log.by || "System"}</b>: {log.action?.replace("_", " ")}{log.note ? ` — ${log.note}` : ""}</> });
    });
    return list.sort((a, b) => new Date(a.at) - new Date(b.at));
  }, [job]);

  if (items.length === 0) return <div className="text-sm text-slate-400 text-center py-4">No activity yet.</div>;

  return (
    <div className="border-l-2 border-slate-200 pl-4 ml-2" data-testid="desk-timeline">
      {items.map((it, i) => (
        <div key={i} className="mb-3.5 relative" data-testid={`desk-timeline-${i}`}>
          <span className="absolute -left-[22px] top-1.5 w-2.5 h-2.5 rounded-full bg-white border-2 border-[#00C4CC]"/>
          <div className="text-[11px] text-slate-500 font-semibold">
            {it.at ? new Date(it.at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
          </div>
          <div className="text-[13px] text-[#0B1F3A] font-medium mt-0.5">{it.msg}</div>
        </div>
      ))}
    </div>
  );
}
